import React from 'react'
import { isEmpty } from 'ramda'
import { useDeviceType } from '~/app/utils/useDeviceType'
import { cn, BottomSheet } from '@yanolja-rnd/nol-design-system'
import { ILeisureWebApiInfoContent } from '~/api/services/leisure-web-api/index.type'
import { zIndexExtra } from '~/app/components/styles/units'

interface InfoListBottomSheetProps {
  /** 바텀 시트 오픈 여부 */
  isOpen: boolean
  /** 바텀 시트 닫기 핸들러 */
  onClose: () => void
  /** 헤더 타이틀 */
  header: string
  /** 바텀 시트 내용 (재귀적 구조를 가진 콘텐츠 배열) */
  contents: ILeisureWebApiInfoContent[]
  /** PC dialog className */
  dialogClassName?: string
}

interface RenderInfoListProps {
  /** 무한대의 depth를 가질 수 있는 info data */
  infos: ILeisureWebApiInfoContent[]
  /** level에 맞는 스타일을 적용하기 위한 parameter */
  level: number
  /** nest될 때마다 indent를 증가시키기 위한 parameter. 최초 함수 호출시에는 default(=0)을 사용하는 것을 권장한다. */
  depth?: number
  /** unique한 key를 만들기 위한 parameter */
  parentIndex?: number
}

/**
 * 구조상 무한대의 depth를 가질 수 있는 info data를 indention, prefix와 함께 재귀적으로 렌더하는 컴포넌트
 * level 0: prefix 없음, level 1: '•' prefix, depth >= 2: '-' prefix, indent 증가
 *
 * @param infos 무한대의 depth를 가질 수 있는 info data
 * @param level level에 맞는 스타일을 적용하기 위한 parameter
 * @param depth nest될 때마다 indent를 증가시키기 위한 parameter. 최초 함수 호출시에는 default(=0)을 사용하는 것을 권장한다.
 * @param parentIndex unique한 key를 만들기 위한 parameter
 */
const RenderInfoList: React.FC<RenderInfoListProps> = ({
  infos,
  level,
  depth = 0,
  parentIndex = 0,
}) => {
  if (isEmpty(infos)) return null

  return (
    <>
      {infos.map(({ content, subContents }, index) => {
        const key = `${depth}-${parentIndex}-${index}`
        // depth >= 2일 때 indent 증가
        const hasIndent = depth >= 2
        const indentClass = hasIndent ? 'ml-16' : ''

        // level 0: prefix 없음
        // level 1: '•' prefix (list-none + ::before)
        // depth >= 2: '-' prefix (list-none + ::before)
        const listStyleClass =
          level === 0
            ? 'list-none'
            : depth >= 2
              ? 'before:content-["-"] relative pl-[16px] before:absolute before:left-0'
              : 'before:content-["•"] relative pl-[16px] before:absolute before:left-0'
        const typographyClass =
          level === 0 ? 'typography-body-14-bold-multiline' : 'typography-body-14-regular-multiline'
        return (
          <ul
            key={key}
            className={cn('flex flex-col gap-y-4 break-words', indentClass, typographyClass)}
          >
            {content && <li className={cn(listStyleClass)}>{content}</li>}
            {subContents && subContents.length > 0 && (
              <RenderInfoList
                infos={subContents}
                depth={depth + 1}
                level={level + 1}
                parentIndex={index}
              />
            )}
          </ul>
        )
      })}
    </>
  )
}

/**
 * 정보를 불릿 리스트 형태로 표시하는 바텀 시트 컴포넌트
 *
 * @description
 * - 주요 용도: 안내 사항, 유의 사항, 정책 등의 정보를 목록 형태로 보여줄 때 사용
 * - 구조상 무한대의 depth를 가질 수 있는 contents data를 재귀적으로 렌더링
 * - 각 항목의 level과 depth에 따라 적절한 prefix와 indent가 자동으로 적용됨
 *   - level 0: prefix 없음
 *   - level 1: '•' prefix
 *   - depth >= 2: '-' prefix, indent 증가
 *
 * @example
 * ```tsx
 * <InfoListBottomSheet
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   header="안내 사항"
 *   contents={[
 *     {
 *       content: "첫 번째 안내 내용입니다.",
 *       subContents: [
 *         { content: "하위 항목 1" },
 *         { content: "하위 항목 2" }
 *       ]
 *     },
 *     { content: "두 번째 안내 내용입니다." }
 *   ]}
 * />
 * ```
 */
const InfoListBottomSheet: React.FC<InfoListBottomSheetProps> = ({
  isOpen,
  onClose,
  header,
  contents,
  dialogClassName,
}) => {
  const deviceType = useDeviceType()
  const isPc = deviceType === 'etc'

  return (
    <BottomSheet
      isOpen={isOpen}
      os={deviceType}
      height="contents"
      header={header}
      onClose={onClose}
      dialogOptions={{
        className: dialogClassName,
        onBackdropClick: () => {
          isPc && onClose()
        },
      }}
      zIndex={zIndexExtra}
    >
      <ul
        className={cn('flex flex-col gap-y-4 px-16 pb-20 pt-8', {
          'pf-pc:px-0 pf-pc:pt-20': isPc,
        })}
      >
        <RenderInfoList infos={contents} level={0} depth={0} />
      </ul>
    </BottomSheet>
  )
}

export default InfoListBottomSheet
